import type { delivery } from '@bb/contracts';
import type { PayloadPreview, TemplateVariable } from '../../../api/client.ts';
import { humanizePath, readPath, textFieldPaths } from './localization.ts';

/**
 * Personalization helpers of the composers (BB-54): inserting a variable's snippet at the cursor, searching and
 * grouping the variable catalog, and reading a previewed payload as people see it.
 */
export type DeliveryChannel = delivery.DeliveryChannel;

export const NAMESPACE_LABELS: Readonly<Record<string, string>> = {
  user: 'User attributes',
  campaign: 'Campaign',
  event_properties: 'Event properties',
  api_trigger_properties: 'API trigger properties',
  context: 'Canvas context',
  params: 'Webhook params',
};
export const namespaceLabel = (namespace: string) => NAMESPACE_LABELS[namespace] ?? humanizePath(namespace);

export const CHANNEL_LABELS: Readonly<Record<DeliveryChannel, string>> = {
  push_android: 'Android push',
  push_ios: 'iOS push',
  push_web: 'Web push',
  content_card: 'Content Card',
  in_app: 'In-app message',
  webhook: 'Webhook',
};

/**
 * `value` with `snippet` put at the cursor (replacing the selected text), and where the cursor goes next: right after
 * the snippet. No known cursor — the end of the text.
 */
export function insertAtCursor(value: string, snippet: string, start?: number | null, end?: number | null): { value: string; caret: number } {
  const clamp = (n: number) => Math.min(Math.max(n, 0), value.length);
  const from = clamp(start ?? value.length);
  const to = Math.max(from, clamp(end ?? from));
  return { value: value.slice(0, from) + snippet + value.slice(to), caret: from + snippet.length };
}

/** How an example value reads in the picker. */
export function formatExample(example: unknown): string {
  if (typeof example === 'string') return `“${example}”`;
  return JSON.stringify(example) ?? String(example);
}

/** Variables matching the search (path, namespace, description, event), case-insensitive. */
export function filterVariables(variables: readonly TemplateVariable[], query: string, namespace = ''): TemplateVariable[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return variables.filter((v) => {
    if (namespace && v.namespace !== namespace) return false;
    const text = [v.namespace, namespaceLabel(v.namespace), v.path, `${v.namespace}.${v.path}`, v.description ?? '', v.event ?? ''].join(' ').toLowerCase();
    return words.every((w) => text.includes(w));
  });
}

/** Variables by namespace, in the order of `namespaces` (what the message may read), the rest after. */
export function groupByNamespace(variables: readonly TemplateVariable[], namespaces: readonly string[] = []): Array<{ namespace: string; variables: TemplateVariable[] }> {
  const order = [...namespaces, ...variables.map((v) => v.namespace).filter((n) => !namespaces.includes(n))];
  return [...new Set(order)].map((namespace) => ({ namespace, variables: variables.filter((v) => v.namespace === namespace) })).filter((g) => g.variables.length > 0);
}

/** A JSON object typed by the editor: empty — nothing; anything but an object — an error. */
export function parseJsonObject(text: string): { value?: Record<string, unknown>; error?: string } {
  if (!text.trim()) return {};
  try {
    const value: unknown = JSON.parse(text);
    if (value && typeof value === 'object' && !Array.isArray(value)) return { value: value as Record<string, unknown> };
  } catch {
    // Reported below.
  }
  return { error: 'Enter a JSON object, for example {"symbol": "EURUSD"}.' };
}

const isObject = (v: unknown): v is Record<string, unknown> => Boolean(v) && typeof v === 'object' && !Array.isArray(v);
const text = (v: unknown) => (typeof v === 'string' && v !== '' ? v : undefined);

/** What a push shows, read from the exact FCM message or APNs request. */
export interface PushView {
  silent: boolean;
  title?: string;
  subtitle?: string;
  body?: string;
  summary?: string;
  imageUrl?: string;
  action?: string;
  buttons: string[];
}

/** A click action of the `bb` payload in words. */
export function actionText(action: unknown): string | undefined {
  if (!isObject(action)) return undefined;
  if (action.kind === 'open_app') return 'Opens the app';
  if (action.kind === 'deep_link') return `Deep link: ${String(action.uri)}`;
  if (action.kind === 'web_url') return `Web page${action.openIn === 'in_app' ? ' (in the app)' : ''}: ${String(action.uri)}`;
  if (action.kind === 'dismiss') return 'Dismisses the notification';
  return typeof action.kind === 'string' ? action.kind : undefined;
}

/**
 * The visible part of a previewed push: Android keeps its text in `data.bb` (a JSON string), iOS in `aps.alert`;
 * `null` when the payload is not a push.
 */
export function pushView(channel: DeliveryChannel, payload: unknown): PushView | null {
  if (!isObject(payload)) return null;
  if (channel === 'push_android') {
    const data = isObject(payload.message) && isObject(payload.message.data) ? payload.message.data : null;
    let bb: Record<string, unknown> = {};
    try {
      const parsed: unknown = typeof data?.bb === 'string' ? JSON.parse(data.bb) : null;
      if (isObject(parsed)) bb = parsed;
    } catch {
      return null;
    }
    const buttons = Array.isArray(bb.btn) ? bb.btn.flatMap((b) => (isObject(b) && typeof b.label === 'string' ? [b.label] : [])) : [];
    return { silent: bb.kind === 'silent', title: text(bb.t), body: text(bb.b), summary: text(bb.sum), imageUrl: text(bb.img), action: actionText(bb.act), buttons };
  }
  if (channel === 'push_ios') {
    const body = isObject(payload.body) ? payload.body : null;
    if (!body) return null;
    const aps = isObject(body.aps) ? body.aps : {};
    const alert = isObject(aps.alert) ? aps.alert : {};
    const bb = isObject(body.bb) ? body.bb : {};
    return { silent: bb.kind === 'silent' || aps['content-available'] === 1, title: text(alert.title), subtitle: text(alert.subtitle), body: text(alert.body), imageUrl: text(bb.img), action: actionText(bb.act), buttons: [] };
  }
  return null;
}

/** `data.bb` of an FCM message decoded, for reading next to the exact payload. */
export function decodedFcmData(payload: unknown): unknown {
  if (!isObject(payload) || !isObject(payload.message) || !isObject(payload.message.data) || typeof payload.message.data.bb !== 'string') return undefined;
  try {
    return JSON.parse(payload.message.data.bb);
  } catch {
    return undefined;
  }
}

/** Text fields of a rendered card or other content, labelled, for the human view. */
export function contentFields(channel: DeliveryChannel, payload: unknown): Array<{ label: string; value: string }> {
  if (!isObject(payload)) return [];
  if (channel === 'content_card') {
    const image = isObject(payload.image) ? text(payload.image.url) : text(payload.image) ?? text(payload.imageUrl);
    return [
      { label: 'Title', value: text(payload.title) },
      { label: 'Description', value: text(payload.description) },
      { label: 'Link text', value: text(payload.linkText) },
      { label: 'Image', value: image },
    ].flatMap((f) => (f.value ? [{ label: f.label, value: f.value }] : []));
  }
  return textFieldPaths(payload).map((path) => ({ label: humanizePath(path), value: String(readPath(payload, path)) }));
}

const ABORT_TEXTS: Readonly<Record<string, string>> = {
  template_aborted: 'The template stopped the message for this user (abort_message): it would not be sent.',
  template_error: 'The template has an error, so the message cannot be rendered.',
  render_invalid: 'The rendered message is not valid, so it would not be sent.',
  empty_after_render: 'Nothing is left to show for this user: the title and the text render empty.',
  payload_too_large: 'The rendered message is bigger than the provider allows, so it would not be sent.',
  invalid_content: 'The message content is not complete: choose an endpoint and fill its parameters.',
  webhook_endpoint_missing: 'The webhook endpoint no longer exists (archived or removed): choose another one.',
  param_missing: 'A required webhook parameter renders empty for this user.',
  param_invalid: 'A webhook parameter does not render as its type (number, boolean, JSON…).',
  param_unknown: 'The message fills a parameter the endpoint does not declare.',
};
export const abortText = (aborted: NonNullable<PayloadPreview['aborted']>) => ABORT_TEXTS[aborted.reason] ?? `The message would not be sent: ${aborted.reason}.`;

/** A preview warning in words. */
export function warningText(warning: PayloadPreview['warnings'][number]): string {
  switch (warning.code) {
    case 'payload_too_large':
      return `The payload is ${warning.detail ?? 'too large'}: over the provider’s 4096-byte limit, so it would not be delivered.`;
    case 'insecure_link':
      return `A link uses http://${warning.detail ? ` (${warning.detail})` : ''}. Use https:// links.`;
    case 'unresolved_variables':
      return `Variables without a value and without a default render empty${warning.detail ? `: ${warning.detail}` : ''}.`;
    case 'masked_personal_data':
      return 'Personal data is shown as •••: seeing it needs the pii.view permission.';
    default:
      return warning.detail ? `${String(warning.code)}: ${warning.detail}` : String(warning.code);
  }
}
